import React, { useState } from 'react';
import { AISupport } from '~/modules/api/api.types';
import { formatDateTime } from '~/modules/utils';
import { TitleValue } from '~/components';
import { DATOCMS_BASE_URL, DATOCMS_ASK_LUNA_QUESTION_ITEM_ID } from '~/modules/config';
import { AnswerComparisonModal } from './AnswerComparisonModal';
import { MatchTypeBadge } from './MatchTypeBadge';

const getDatoCMSUrl = (datocmsQuestionId: string): string => {
  if (!DATOCMS_BASE_URL || !DATOCMS_ASK_LUNA_QUESTION_ITEM_ID) {
    return '#';
  }
  return `${DATOCMS_BASE_URL}/editor/item_types/${DATOCMS_ASK_LUNA_QUESTION_ITEM_ID}/items/${datocmsQuestionId}`;
};

const getSortedEvents = (question: AISupport.AutoMatchQuestionRow) => {
  const events: Array<{
    type: 'decision';
    timestamp: string;
    data: AISupport.AutoPublishDecisionRow;
  } | {
    type: 'match';
    timestamp: string;
    data: AISupport.QuestionMatchRow;
    linkedDecisions: AISupport.AutoPublishDecisionRow[];
  }> = [];

  // Group all decisions by match_id
  const decisionsByMatchId = new Map<string, AISupport.AutoPublishDecisionRow[]>();
  if (Array.isArray(question.decisions)) {
    question.decisions.forEach((decision) => {
      if (decision.match_id) {
        if (!decisionsByMatchId.has(decision.match_id)) {
          decisionsByMatchId.set(decision.match_id, []);
        }
        decisionsByMatchId.get(decision.match_id)!.push(decision);
      }
    });
  }

  // Sort decisions within each match by decision_date (newest first)
  decisionsByMatchId.forEach((decisions) => {
    decisions.sort((a, b) => new Date(b.decision_date).getTime() - new Date(a.decision_date).getTime());
  });

  // Add matches with all their linked decisions
  if (Array.isArray(question.matches)) {
    question.matches.forEach((match) => {
      const linkedDecisions = decisionsByMatchId.get(match.id) || [];
      
      events.push({
        type: 'match',
        timestamp: match.matched_at,
        data: match,
        linkedDecisions,
      });
    });
  }

  // Add decisions not linked to matches (decisions without match_id)
  if (Array.isArray(question.decisions)) {
    question.decisions.forEach((decision) => {
      if (!decision.match_id) {
        events.push({
          type: 'decision',
          timestamp: decision.decision_date,
          data: decision,
        });
      }
    });
  }

  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

interface QuestionCardProps {
  question: AISupport.AutoMatchQuestionRow;
  onQueueAutoPopulate: (questionId: string) => void;
  onQueueAutoPublish: (questionId: string, questionVersion: string, matchId: string) => void;
  isQueueingPopulate: boolean;
  isQueueingPublish: boolean;
}

export const QuestionCard = ({
  question,
  onQueueAutoPopulate,
  onQueueAutoPublish,
  isQueueingPopulate,
  isQueueingPublish,
}: QuestionCardProps) => {
  const [comparisonModal, setComparisonModal] = useState<{
    isOpen: boolean;
    currentAnswer: any;
    matchedAnswer: any;
    currentQuestionId: string;
    matchedQuestionId: string;
    questionText: string;
  }>({
    isOpen: false,
    currentAnswer: null,
    matchedAnswer: null,
    currentQuestionId: '',
    matchedQuestionId: '',
    questionText: '',
  });

  const events = getSortedEvents(question);
  const isPublished = question.question_data?.status === 'published' || question.question_data?.status === 'updated';

  const openComparisonModal = (match: AISupport.QuestionMatchRow) => {
    setComparisonModal({
      isOpen: true,
      currentAnswer: question.question_data?.attributes?.answer?.document,
      matchedAnswer: match.best_match_question_metadata?.attributes?.answer?.document,
      currentQuestionId: question.question_id,
      matchedQuestionId: match.best_match_question_id,
      questionText: question.question_data?.attributes?.question || '',
    });
  };

  const closeComparisonModal = () => {
    setComparisonModal(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <div className="card bg-base-100 shadow-lg border border-gray-200">
      <div className="card-body">
        {/* Question Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="card-title text-lg font-mono">
              <a 
                href={getDatoCMSUrl(question.question_data.datocms_question_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {question.question_id}
              </a>
            </h3>
            <p className="text-gray-600 mt-1">
              {question.question_data?.attributes?.question || 'No question text'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`badge ${
              question.question_data.attributes.rejected_reason ? "badge-error" : 
              question.question_data?.status === 'published' 
                ? 'badge-success' 
                : question.question_data?.status === 'draft' || question.question_data?.status === 'updated'
                ? 'badge-warning'
                : 'badge-neutral'
            } capitalize`}>
              {question.question_data.attributes.rejected_reason ? "Rejected" : question.question_data?.status || 'unknown'}
            </span>
            {question.question_data.status === 'draft' && !question.matches.length && (
              <button
              className="btn btn-sm btn-primary"
              onClick={() => onQueueAutoPopulate(question.question_id)}
              disabled={isQueueingPopulate}
              >
                {isQueueingPopulate ? 'Queueing...' : 'Queue Auto-Populate'}
              </button>
            )}
          </div>
        </div>

        {/* Question Metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <TitleValue 
            title="Created" 
            value={formatDateTime(question.question_data?.created_at)} 
          />
          <TitleValue 
            title="Published" 
            value={question.question_data?.published_at 
              ? formatDateTime(question.question_data.published_at)
              : '-'
            } 
          />
          <TitleValue 
            title="Workflow Stage" 
            value={
              <span className="capitalize">
                {question.question_data?.workflow_stage ?? 'no stage'}
              </span>
            } 
          />
        </div>

        {/* Events Timeline */}
        {events.length > 0 && (
          <div className="pt-2">
            <div className="space-y-3">
              {events.map((event, index) => {
                if (event.type === 'match') {
                  return (
                    <div key={index} className="space-y-2">
                      {/* Match Header */}
                      <div className="flex items-start gap-3 p-3 bg-gray-100 rounded-lg">
                        <div className="flex-1">
                          <div className="flex flex-1 gap-2 mb-2">
                            <div className="flex flex-col items-start">
                              <div>
                                <span className="text-sm font-semibold">
                                  {event.data.datocms_user_id.startsWith("system") ? "System Match" : "User Match"}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDateTime(event.timestamp)}
                              </span>
                              <a href={getDatoCMSUrl(event.data.best_match_question_id)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                                {event.data.best_match_question_id}
                              </a>
                              <div className="mt-1">
                              <MatchTypeBadge
                                matchType={event.data.matchType as any}
                                fallbackText="unknown"
                                size="sm"
                              />
                              </div>
                            </div>

                            <div className="flex-1">
                              <div className="flex flex-row justify-around flex-wrap gap-4">
                                <TitleValue 
                                  title="Current Similarity" 
                                  value={event.data.cosine_distance_current_answer?.toFixed(4) ?? '-'} 
                                  className="min-w-[140px]"
                                />
                                {/* Match-level action buttons */}
                                <div className="flex flex-col items-center gap-2">
                                  <button
                                    className="btn btn-xs btn-info"
                                    onClick={() => openComparisonModal(event.data)}
                                  >
                                    Compare Answers
                                  </button>
                                  <button
                                    className="btn btn-xs btn-secondary"
                                    onClick={() => onQueueAutoPublish(
                                      question.question_id, 
                                      event.linkedDecisions[0]?.datocms_record_version ?? question.question_data.current_version, 
                                      event.data.id,
                                    )}
                                    disabled={isQueueingPublish}
                                    >
                                    {isQueueingPublish ? 'Queueing...' : 'Queue Auto-Publish Check'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decisions for this match */}
                      {event.linkedDecisions.length > 0 && (
                        <div className="ml-4 space-y-2">
                          {event.linkedDecisions.map((decision, decisionIndex) => {
                            const isSuperseded = decision.decision_stage === 'superseded';
                            const isDevStrategy = decision.decision_strategy?.toLowerCase().includes('dev');
                            
                            console.log(event.data.perStrategyMatchTypes)
                            // Find the match type for this decision from perStrategyMatchTypes
                            const strategyMatchType = event.data.perStrategyMatchTypes?.find(
                              strategyMatch => strategyMatch.decision_id === decision.id
                            )?.matchType;
                            
                            return (
                              <div 
                                key={`${index}-${decisionIndex}`} 
                                className={`flex items-start gap-3 p-3 rounded-lg border-l-4 ${
                                  isDevStrategy 
                                    ? 'bg-orange-50 border-orange-300' 
                                    : 'bg-gray-50 border-blue-200'
                                } ${
                                  isSuperseded ? 'opacity-60' : 'opacity-100'
                                }`}
                              >
                                <div className="flex-1">
                                  <div className="flex flex-1 gap-4 mb-3">
                                    <div className="flex flex-col items-start min-w-[200px]">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold">
                                          {decision.trigger}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-500 mb-2">
                                        {formatDateTime(decision.decision_date)}
                                      </span>
                                      <div className="flex gap-2 mb-2">
                                        {isDevStrategy && (
                                          <span className="badge badge-xs badge-warning">DEV</span>
                                        )}
                                        {isSuperseded && (
                                          <span className="badge badge-xs badge-neutral">Superseded</span>
                                        )}
                                      </div>
                                      <MatchTypeBadge 
                                        matchType={strategyMatchType as any} 
                                        fallbackText="unknown"
                                        size="sm"
                                      />
                                    </div>

                                    <div className="flex-1">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <TitleValue 
                                          title="Strategy" 
                                          value={decision.decision_strategy} 
                                          className="min-w-[100px]"
                                        />
                                        <TitleValue 
                                          title="Stage" 
                                          value={decision.decision_stage} 
                                          className="min-w-[80px]"
                                        />
                                        <TitleValue 
                                          title="Decision" 
                                          value={
                                            <span className={`badge badge-sm ${
                                              decision.should_publish ? 'badge-success' : 'badge-error'
                                            }`}>
                                              {decision.should_publish ? 'Should Publish' : 'Should Not Publish'}
                                            </span>
                                          } 
                                          className="min-w-[120px]"
                                        />
                                        <TitleValue 
                                          title="Stock Answer Count" 
                                          value={decision.stock_answer_count ?? '-'} 
                                          className="min-w-[120px]"
                                        />
                                      </div>
                                      {(decision.llm_gate_reason || decision.system_reason || (decision.decision_stage === 'skipped' && decision.auto_publish_error)) && (
                                        <div className="mt-3">
                                          {decision.llm_gate_reason && (
                                            <div className="mb-2">
                                              <div className="text-xs font-medium text-gray-600 mb-1">LLM Gate Reason:</div>
                                              <div className="text-xs text-gray-700 bg-gray-100 p-2 rounded border">
                                                {decision.llm_gate_reason}
                                              </div>
                                            </div>
                                          )}
                                          {decision.system_reason && (
                                            <div className="mb-2">
                                              <div className="text-xs font-medium text-gray-600 mb-1">System Reason:</div>
                                              <div className="text-xs text-gray-700 bg-gray-100 p-2 rounded border">
                                                {decision.system_reason}
                                              </div>
                                            </div>
                                          )}
                                          {decision.decision_stage === 'skipped' && decision.auto_publish_error && (
                                            <div>
                                              <div className="text-xs font-medium text-gray-600 mb-1">Auto Publish Error:</div>
                                              <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
                                                {decision.auto_publish_error}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Standalone decision (not linked to a match)
                  const isDevStrategy = event.data.decision_strategy?.toLowerCase().includes('dev');
                  const isSuperseded = event.data.decision_stage === 'superseded';
                  
                  // For standalone decisions, we don't have perStrategyMatchTypes, so matchType will be null
                  const strategyMatchType = null;
                  
                  return (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-100 rounded-lg">
                      <div className="flex-1">
                        <div className="flex flex-1 gap-4 mb-3">
                          <div className="flex flex-col items-start min-w-[200px]">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold">
                                {event.data.trigger}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 mb-2">
                              {formatDateTime(event.timestamp)}
                            </span>
                            <div className="flex gap-2 mb-2">
                              {isDevStrategy && (
                                <span className="badge badge-xs badge-warning">DEV</span>
                              )}
                              {isSuperseded && (
                                <span className="badge badge-xs badge-neutral">Superseded</span>
                              )}
                            </div>
                            <MatchTypeBadge 
                              matchType={strategyMatchType as any} 
                              fallbackText="N/A"
                              size="sm"
                            />
                          </div>

                          <div className="flex-1">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <TitleValue 
                                title="Strategy" 
                                value={event.data.decision_strategy} 
                                className="min-w-[100px]"
                              />
                              <TitleValue 
                                title="Stage" 
                                value={event.data.decision_stage} 
                                className="min-w-[80px]"
                              />
                              <TitleValue 
                                title="Decision" 
                                value={
                                  <span className={`badge badge-sm ${
                                    event.data.should_publish ? 'badge-success' : 'badge-error'
                                  }`}>
                                    {event.data.should_publish ? 'Should Publish' : 'Should Not Publish'}
                                  </span>
                                } 
                                className="min-w-[120px]"
                              />
                              <TitleValue 
                                title="Stock Answer Count" 
                                value={event.data.stock_answer_count ?? '-'} 
                                className="min-w-[120px]"
                              />
                            </div>
                            {(event.data.llm_gate_reason || event.data.system_reason || (event.data.decision_stage === 'skipped' && event.data.auto_publish_error)) && (
                              <div className="mt-3">
                                {event.data.llm_gate_reason && (
                                  <div className="mb-2">
                                    <div className="text-xs font-medium text-gray-600 mb-1">LLM Gate Reason:</div>
                                    <div className="text-xs text-gray-700 bg-gray-100 p-2 rounded border">
                                      {event.data.llm_gate_reason}
                                    </div>
                                  </div>
                                )}
                                {event.data.system_reason && (
                                  <div className="mb-2">
                                    <div className="text-xs font-medium text-gray-600 mb-1">System Reason:</div>
                                    <div className="text-xs text-gray-700 bg-gray-100 p-2 rounded border">
                                      {event.data.system_reason}
                                    </div>
                                  </div>
                                )}
                                {event.data.decision_stage === 'skipped' && event.data.auto_publish_error && (
                                  <div>
                                    <div className="text-xs font-medium text-gray-600 mb-1">Auto Publish Error:</div>
                                    <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
                                      {event.data.auto_publish_error}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
      </div>

      {/* Answer Comparison Modal */}
      <AnswerComparisonModal
        isOpen={comparisonModal.isOpen}
        onClose={closeComparisonModal}
        currentAnswer={comparisonModal.currentAnswer}
        matchedAnswer={comparisonModal.matchedAnswer}
        currentQuestionId={comparisonModal.currentQuestionId}
        matchedQuestionId={comparisonModal.matchedQuestionId}
        questionText={comparisonModal.questionText}
      />
    </div>
  );
};
